import React, { useState, useEffect } from 'react';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

export interface CreateAllianceForm {
  name: string;
  slogan: string;
  presentationText: string;
}

interface CreateAllianceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAllianceForm) => void;
  /** Noms déjà utilisés (en minuscules) pour vérifier l'unicité */
  existingNames?: string[];
}

const FORBIDDEN_WORD = 'opsecours';

const CreateAllianceModal: React.FC<CreateAllianceModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  existingNames = [],
}) => {
  const { t } = usePageTranslation('game', 'AlliancePage');
  const [name, setName] = useState('');
  const [slogan, setSlogan] = useState('');
  const [presentationText, setPresentationText] = useState('');
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setNameError('');
    const trimmedName = name.trim();
    if (!trimmedName) return;
    if (trimmedName.toLowerCase().includes(FORBIDDEN_WORD)) {
      setNameError(t('create.nameForbidden'));
      return;
    }
    if (existingNames.includes(trimmedName.toLowerCase())) {
      setNameError(t('create.nameTaken'));
      return;
    }
    onSubmit({ name: trimmedName, slogan: slogan.trim(), presentationText: presentationText.trim() });
    setName('');
    setSlogan('');
    setPresentationText('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-lg border border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">{t('create.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
            aria-label="Fermer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('create.name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(''); }}
              required
              className={`w-full px-4 py-2 rounded-lg bg-gray-800 border text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 ${nameError ? 'border-red-500' : 'border-gray-600'}`}
              placeholder={t('params.allianceNamePlaceholder')}
            />
            {nameError && (
              <p className="mt-1.5 text-sm text-red-400" role="alert">{nameError}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('create.slogan')}</label>
            <input
              type="text"
              value={slogan}
              onChange={(e) => setSlogan(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500"
              placeholder={t('params.sloganPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('create.presentation')}</label>
            <textarea
              value={presentationText}
              onChange={(e) => setPresentationText(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 resize-y"
              placeholder={t('params.presentationPlaceholder')}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium"
            >
              {t('create.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            >
              {t('create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAllianceModal;
